"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterVariantCount } from "@/components/ui/filters";
import type { VariantFilterValue } from "@/lib/api/types";
import { formatEntityForms, type VariantField } from "@/lib/search/entity-variants";
import { logo } from "@/lib/logo-dev";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { cn } from "@/lib/utils";
import { FilterValueActions } from "../actions";
import { ScopeDropdown } from "./internal/scope-dropdown";
import { VariantsDropdown } from "./internal/variants-dropdown";
import { FilterMonthYearInput } from "./month-year-input";

export function FilterCompanyInput({
  company,
  variantsField,
  onChange,
  onRemove,
}: {
  company: VariantFilterValue;
  /** Opt-in: lets the chip carry alias variants matched from this field. */
  variantsField?: VariantField;
  onChange: (updated: VariantFilterValue) => void;
  onRemove: () => void;
}) {
  const name = titleCasePreserveAcronyms(company.text ?? "");
  const dateEnabled = company.date_enabled ?? false;
  const scope = company.scope ?? "cp";
  const isExcluded = company.mode === "exclude";

  const logoUrl = logo({ domain: company.domain, name });

  return (
    <div
      className={cn(
        "border rounded-md overflow-clip",
        isExcluded ? "border-destructive/30" : "border-border",
      )}
    >
      <div className="flex items-center justify-between bg-contrast/4 h-[34px] px-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {logoUrl && (
            <Avatar className="size-4 shrink-0 rounded-[3px] shadow-[0px_0.35px_0.7px_0px_rgba(0,0,0,0.05)]">
              <AvatarImage src={logoUrl} alt="" />
              <AvatarFallback />
              {/* Inner rim sits above the image (inset shadows are painted
                  below element content, so we overlay it instead). */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-0.35px_0px_0px_rgba(0,0,0,0.08)]"
              />
            </Avatar>
          )}
          <span
            title={company.variants?.length ? formatEntityForms({ text: name, variants: company.variants }) : undefined}
            className={cn(
              "text-[13px] font-normal truncate tracking-[-0.13px]",
              isExcluded
                ? "text-destructive/80 dark:text-red-400/80 line-through"
                : "text-foreground/80",
            )}
          >
            {name}
            <FilterVariantCount count={company.variants?.length ?? 0} />
          </span>
        </div>
        <FilterValueActions
          excluded={isExcluded}
          required={company.required ?? false}
          onToggleRequired={() =>
            onChange({ ...company, required: !company.required })
          }
          onToggleExclude={() =>
            onChange({ ...company, mode: isExcluded ? "include" : "exclude" })
          }
          onRemove={onRemove}
        />
      </div>
      <div className="flex items-center justify-between bg-contrast/8 border-t border-border h-7 px-2">
        <div className="flex gap-1 items-center">
          <Checkbox
            checked={dateEnabled}
            onChange={() =>
              onChange({ ...company, date_enabled: !dateEnabled })
            }
          />
          <span className="text-[11px] text-foreground/60 tracking-[-0.1px]">
            From
          </span>
          <FilterMonthYearInput
            value={company.from_date}
            onChange={(d) =>
              onChange({ ...company, from_date: d, date_enabled: true })
            }
          />
          <span className="text-[11px] text-foreground/60 tracking-[-0.1px]">
            to
          </span>
          <FilterMonthYearInput
            value={company.to_date}
            onChange={(d) =>
              onChange({ ...company, to_date: d, date_enabled: true })
            }
          />
        </div>
        <div className="flex items-center gap-3">
          {variantsField && (
            <VariantsDropdown field={variantsField} item={company} onChange={onChange} />
          )}
          <ScopeDropdown
            value={scope}
            onChange={(v) => onChange({ ...company, scope: v })}
          />
        </div>
      </div>
    </div>
  );
}
